import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TrendingUp, Euro, Clock, CheckCircle, Download, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const PartnerProvisionenPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const partnerId = searchParams.get('partner_id') || localStorage.getItem('partner_id');
  
  const [summary, setSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [monthFilter, setMonthFilter] = useState('current');
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!partnerId) {
      navigate('/partner/registrieren');
      return;
    }
    loadData();
  }, [partnerId, monthFilter]);

  const getMonthParam = () => {
    const now = new Date();
    if (monthFilter === 'current') return now.toISOString().slice(0, 7);
    if (monthFilter === 'last') {
      now.setMonth(now.getMonth() - 1);
      return now.toISOString().slice(0, 7);
    }
    if (monthFilter === 'last3') return null; // Get all from last 3 months
    return null;
  };

  const loadData = async () => {
    try {
      const monthParam = getMonthParam();
      const params = monthParam ? `?month=${monthParam}` : '';
      
      const summaryRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/provision/summary/${partnerId}${params}`);
      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      const casesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/provision/cases/${partnerId}${params}`);
      const casesData = await casesRes.json();
      setCases(casesData);
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const monthParam = getMonthParam();
      const params = monthParam ? `?month=${monthParam}` : '';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/provision/export/${partnerId}${params}`);
      const data = await response.json();
      
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
    } catch (error) {
      alert('Export fehlgeschlagen');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      eingegangen: 'bg-gray-100 text-gray-700',
      in_bearbeitung: 'bg-yellow-100 text-yellow-700',
      abgeschlossen: 'bg-brand-green/10 text-brand-green'
    };
    return badges[status] || 'bg-gray-100 text-gray-600';
  };

  const getPayoutBadge = (payout) => {
    const badges = {
      open: 'bg-gray-100 text-gray-700',
      ready: 'bg-blue-100 text-blue-700',
      paid: 'bg-brand-green/10 text-brand-green'
    };
    return badges[payout] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Provisionen & Auszahlungen</h1>
            <Button onClick={() => navigate(`/portal/dashboard?partner_id=${partnerId}`)} variant="outline" className="border-gray-300">
              Zurück
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Fälle dieses Monats</p>
                <p className="text-2xl font-bold text-gray-900">{summary?.current_month_cases || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Provisionslevel</p>
                <p className="text-2xl font-bold text-brand-green">{summary?.provision_level || 30}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Offen</p>
                <p className="text-2xl font-bold text-gray-900">{summary?.open_total_amount?.toFixed(2) || '0.00'} €</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Ausgezahlt</p>
                <p className="text-2xl font-bold text-brand-green">{summary?.paid_total_amount?.toFixed(2) || '0.00'} €</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              <option value="current">Dieser Monat</option>
              <option value="last">Letzter Monat</option>
              <option value="last3">Letzte 3 Monate</option>
            </select>
            <Button onClick={handleExport} variant="outline" className="border-2 border-gray-300">
              <Download className="w-4 h-4 mr-2" />
              CSV Export
            </Button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Datum</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Kennzeichen</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status Fall</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Reguliert Netto</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Provision %</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Provision Betrag</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Auszahlung</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cases.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      Keine Fälle im gewählten Zeitraum
                    </td>
                  </tr>
                ) : (
                  cases.map(c => (
                    <tr key={c.case_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {c.submitted_at ? new Date(c.submitted_at).toLocaleDateString('de-DE') : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {c.license_plate || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(c.status)}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                        {c.reguliert_netto ? `${c.reguliert_netto.toFixed(2)} €` : '–'}
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-brand-green font-semibold">
                        {c.partner_provision_percent}%
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-bold text-brand-green">
                        {c.partner_provision_amount ? `${c.partner_provision_amount.toFixed(2)} €` : '–'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPayoutBadge(c.payout_status)}`}>
                          {c.payout_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => setSelectedCase(c)}
                          className="text-brand-green hover:text-brand-green-dark font-semibold text-sm"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 bg-gray-100 border border-gray-200 rounded-xl p-6">
          <p className="text-sm text-gray-700 leading-relaxed">
            <strong>Hinweis:</strong> Die Provision wird auf Basis des tatsächlich regulierten Netto-Grundhonorars berechnet. Kürzungen werden berücksichtigt. Die Auszahlung erfolgt nach erfolgreicher Regulierung und Freigabe.
          </p>
        </div>
      </div>

      {selectedCase && (
        <DetailModal caseData={selectedCase} onClose={() => setSelectedCase(null)} />
      )}
    </div>
  );
};

const DetailModal = ({ caseData, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={onClose}>
    <div className="bg-white rounded-2xl p-10 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Fall-Details (Read Only)</h2>
      
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600 mb-1">Kennzeichen</p>
            <p className="font-semibold text-gray-900">{caseData.license_plate || '-'}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Status</p>
            <p className="font-semibold text-gray-900">{caseData.status}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Netto Grundhonorar</p>
            <p className="font-semibold text-gray-900">{caseData.reguliert_netto ? `${(caseData.reguliert_netto + (caseData.kuerzung || 0)).toFixed(2)} €` : '–'}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Kürzung</p>
            <p className="font-semibold text-gray-900">{caseData.kuerzung ? `${caseData.kuerzung.toFixed(2)} €` : '0.00 €'}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Reguliert Netto</p>
            <p className="font-bold text-brand-green text-lg">{caseData.reguliert_netto ? `${caseData.reguliert_netto.toFixed(2)} €` : '–'}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Ihr Provisionslevel</p>
            <p className="font-bold text-brand-green text-lg">{caseData.partner_provision_percent}%</p>
          </div>
        </div>

        <div className="bg-brand-green/5 border border-brand-green/20 rounded-xl p-6">
          <p className="text-sm text-gray-600 mb-2">Ihre Provision</p>
          <p className="text-3xl font-bold text-brand-green">
            {caseData.partner_provision_amount ? `${caseData.partner_provision_amount.toFixed(2)} €` : '–'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600 mb-1">Auszahlung Status</p>
            <p className="font-semibold text-gray-900">{caseData.payout_status}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Ausgezahlt am</p>
            <p className="font-semibold text-gray-900">
              {caseData.payout_paid_at ? new Date(caseData.payout_paid_at).toLocaleDateString('de-DE') : '–'}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Button onClick={onClose} className="w-full bg-gray-900 hover:bg-gray-800 text-white">
          Schließen
        </Button>
      </div>
    </div>
  </div>
);

export default PartnerProvisionenPage;
